import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';

const AuthContext = createContext(null);

/**
 * Utilitaires de stockage du token.
 * ⚠️  En production, préférez des httpOnly cookies (nécessite une config serveur CORS).
 * sessionStorage est utilisé ici :
 *   - il n'est PAS accessible depuis les iframes cross-origin
 *   - il est effacé à la fermeture de l'onglet
 *   - il réduit la fenêtre d'attaque XSS vs localStorage (persistant)
 */
const TOKEN_KEY = 'ose_auth';

function storeToken(token) {
  try {
    // sessionStorage : éphémère (onglet), non partagé entre iframes
    sessionStorage.setItem(TOKEN_KEY, token);
    // Garder aussi dans localStorage pour la persistance entre onglets
    // (compromis UX : l'utilisateur n'a pas à se reconnecter à chaque onglet)
    localStorage.setItem(TOKEN_KEY, token);
  } catch (e) {
    console.error('[Auth] Impossible de stocker le token :', e);
  }
}

function readToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

function clearToken() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    // Nettoyer aussi l'ancienne clé si elle existe
    localStorage.removeItem('ose_token');
    sessionStorage.removeItem('ose_token');
  } catch { /* silent */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => readToken());
  const [loading, setLoading] = useState(true);
  // Flag: skip fetchMe() when login() already provided the user object
  const skipFetchRef = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        try {
          const text = await res.text();
          const data = text ? JSON.parse(text) : null;
          if (data) setUser(data);
        } catch {
          // Corps non parseable → ignorer, session reste active
        }
      } else if (res.status === 401) {
        logout(); // Token vraiment invalide ou expiré
      }
      // 429 ou autre erreur → on garde la session existante, pas de logout
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('[Auth] fetchMe error:', e);
      // Erreur réseau → ne pas déconnecter
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => {
    if (token) {
      if (skipFetchRef.current) {
        skipFetchRef.current = false;
        setLoading(false);
        return;
      }
      fetchMe();
    } else {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = useCallback((tokenValue, userData) => {
    skipFetchRef.current = true;
    storeToken(tokenValue);
    setUser(userData);
    setToken(tokenValue);
  }, []);

  const logout = useCallback(() => {
    skipFetchRef.current = false;
    clearToken();
    setToken(null);
    setUser(null);
  }, []);

  const isAdmin = user?.role === 'admin';
  const isLivreur = user?.role === 'livreur';
  const isClient = user?.role === 'client';

  // useMemo évite de recréer l'objet de contexte à chaque render
  const contextValue = useMemo(
    () => ({ user, token, loading, login, logout, isAdmin, isLivreur, isClient }),
    [user, token, loading, login, logout, isAdmin, isLivreur, isClient]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
